import React, { useState, useEffect, useCallback } from "react";
import { Table, Pagination, Spinner, Alert, Badge, Modal, Button, TextInput } from "flowbite-react";
import { auth, database } from "../firebase";
import { ref, get } from "firebase/database";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DetailedTradeView from "./AutoTrades/DetailedTradeView";
import { useThemeMode } from "flowbite-react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import axios from "axios";
import HighlightedFlowbiteDatepicker from "./AutoHistory/HighlightedFlowbiteDatepicker";

const AutoHistory = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalTrades, setTotalTrades] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tradeDates, setTradeDates] = useState([]);
  const [instrumentFilter, setInstrumentFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");

  const tradesPerPage = 20;
  const { mode } = useThemeMode();
  const darkMode = mode === "dark";

  const fetchTrades = useCallback(
    async (page) => {
      setLoading(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user) throw new Error("User not authenticated");

        const tradesRef = ref(database, `users/${user.uid}/autotrades`);
        const snapshot = await get(tradesRef);

        if (snapshot.exists()) {
          let allTrades = [];
          snapshot.forEach((yearSnapshot) => {
            yearSnapshot.forEach((monthSnapshot) => {
              monthSnapshot.forEach((daySnapshot) => {
                daySnapshot.forEach((tradeSnapshot) => {
                  allTrades.push({
                    id: tradeSnapshot.key,
                    ...tradeSnapshot.val(),
                    year: yearSnapshot.key,
                    month: monthSnapshot.key,
                    day: daySnapshot.key,
                  });
                });
              });
            });
          });

          // Filter trades by instrument
          if (appliedFilter) {
            allTrades = allTrades.filter((trade) => trade.epic.toLowerCase().includes(appliedFilter.toLowerCase()));
          }

          // Sort trades by timestamp in descending order
          allTrades.sort((a, b) => b.timestamp - a.timestamp);

          // Paginate the trades
          const startIndex = (page - 1) * tradesPerPage;
          const endIndex = startIndex + tradesPerPage;
          const paginatedTrades = allTrades.slice(startIndex, endIndex);

          setTrades(paginatedTrades);
          setTotalTrades(allTrades.length);
        } else {
          setTrades([]);
          setTotalTrades(0);
        }
      } catch (err) {
        console.error("Error fetching trades:", err);
        setError("Failed to fetch trades. Please try again later.");
      } finally {
        setLoading(false);
      }
    },
    [tradesPerPage, appliedFilter]
  );

  const fetchTotalTrades = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const tradesRef = ref(database, `users/${user.uid}/autotrades`);
      const snapshot = await get(tradesRef);

      if (snapshot.exists()) {
        let count = 0;
        snapshot.forEach((childSnapshot) => {
          if (typeof childSnapshot.val() === "object" && childSnapshot.val() !== null) {
            count++;
          }
        });
        setTotalTrades(count);
      } else {
        setTotalTrades(0);
      }
    } catch (err) {
      console.error("Error fetching total trades:", err);
    }
  }, []);

  const handleDeleteOldTrades = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const year = selectedDate.getFullYear().toString();
      const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
      const day = selectedDate.getDate().toString().padStart(2, "0");

      const response = await axios.post("https://pdf-extractor-api-blond.vercel.app/delete-trades/day", {
        uid: user.uid,
        year: year,
        month: month,
        day: day,
      });

      if (response.data.success) {
        console.log(`Deleted: ${response.data.message}`);
        // Refresh the trades list
        fetchTrades(1);
      } else {
        throw new Error("Failed to delete old trades");
      }
    } catch (err) {
      console.error("Error deleting old trades:", err);
      setError("Failed to delete old trades. Please try again later.");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const fetchTradeDates = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const tradesRef = ref(database, `users/${user.uid}/autotrades`);
      const snapshot = await get(tradesRef);

      if (snapshot.exists()) {
        const dates = [];
        snapshot.forEach((yearSnapshot) => {
          yearSnapshot.forEach((monthSnapshot) => {
            monthSnapshot.forEach((daySnapshot) => {
              const date = new Date(parseInt(yearSnapshot.key), parseInt(monthSnapshot.key) - 1, parseInt(daySnapshot.key));
              dates.push(date);
            });
          });
        });
        setTradeDates(dates);
      }
    } catch (err) {
      console.error("Error fetching trade dates:", err);
    }
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleFilterChange = (e) => {
    setInstrumentFilter(e.target.value);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setAppliedFilter(instrumentFilter);
    setCurrentPage(1); // Reset to first page when filter is applied
  };

  useEffect(() => {
    fetchTradeDates();
  }, [fetchTradeDates]);

  useEffect(() => {
    fetchTotalTrades();
  }, [fetchTotalTrades]);

  useEffect(() => {
    fetchTrades(currentPage);
  }, [fetchTrades, currentPage, appliedFilter]);

  const indexOfLastTrade = currentPage * tradesPerPage;
  const indexOfFirstTrade = indexOfLastTrade - tradesPerPage;
  const currentTrades = trades;

  const MiniChart = ({ priceData }) => {
    if (!priceData || !priceData.prices) return null;

    const options = {
      chart: {
        type: "line",
        height: 60,
        width: 120,
        backgroundColor: "transparent",
      },
      title: { text: null },
      xAxis: { visible: false },
      yAxis: { visible: false },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: { enabled: false },
      series: [
        {
          data: priceData.prices.map((price) => [
            new Date(price.snapshotTimeUTC).getTime(),
            (price.closePrice.bid + price.closePrice.ask) / 2,
          ]),
          color: darkMode ? "#90caf9" : "#4bc0c0",
          lineWidth: 1,
        },
      ],
      plotOptions: {
        series: {
          animation: false,
        },
      },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
  };

  const DirectionBadge = ({ direction }) => {
    const color = direction === "BUY" ? "success" : "failure";
    return <Badge color={color}>{direction}</Badge>;
  };

  if (loading) return <Spinner aria-label="Loading trades" />;
  if (error) return <Alert color="failure">{error}</Alert>;

  return (
    <main className={`p-4 ${darkMode ? "bg-slate-900 text-white" : "bg-slate-100 text-black"} min-h-screen`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-2xl font-bold ${darkMode ? "text-slate-200" : "text-slate-800"}`}>Auto Trades</h2>
        <div className="flex items-center mb-4">
          <form onSubmit={handleFilterSubmit} className="flex items-center mr-2">
            <TextInput
              type="text"
              placeholder="Filter by instrument"
              value={instrumentFilter}
              onChange={handleFilterChange}
              className="mr-2"
            />
            <Button type="submit">Apply Filter</Button>
          </form>
          <HighlightedFlowbiteDatepicker
            selectedDate={selectedDate}
            onChange={setSelectedDate}
            highlightedDates={tradeDates}
            className="mr-2"
          />
          <Button color="failure" onClick={() => setIsDeleteModalOpen(true)}>
            Delete Trades on this Date
          </Button>
        </div>
      </div>
      {loading ? (
        <Spinner aria-label="Loading trades" />
      ) : error ? (
        <Alert color="failure">{error}</Alert>
      ) : (
        <>
          <Table striped>
            <Table.Head>
              <Table.HeadCell>Direction</Table.HeadCell>
              <Table.HeadCell>Ticker</Table.HeadCell>
              <Table.HeadCell>Date</Table.HeadCell>
              <Table.HeadCell>Type</Table.HeadCell>
              <Table.HeadCell>Size</Table.HeadCell>
              <Table.HeadCell>Price</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell>Chart</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {currentTrades.map((trade) => (
                <Table.Row
                  key={trade.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800 cursor-pointer hover:bg-cyan-700 dark:hover:bg-cyan-700"
                  onClick={() => {
                    setSelectedTrade(trade);
                    setIsModalOpen(true);
                  }}
                >
                  <Table.Cell>
                    <DirectionBadge direction={trade.direction} />
                  </Table.Cell>
                  <Table.Cell>{trade.epic}</Table.Cell>
                  <Table.Cell>{new Date(trade.timestamp).toLocaleString()}</Table.Cell>
                  <Table.Cell>{trade.type}</Table.Cell>
                  <Table.Cell>{trade.size}</Table.Cell>
                  <Table.Cell>{trade.price}</Table.Cell>
                  <Table.Cell>{trade.status}</Table.Cell>
                  <Table.Cell className="w-1/4">
                    <MiniChart priceData={trade.priceData} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <div className="flex items-center justify-center text-center mt-4">
            <Pagination
              currentPage={currentPage}
              onPageChange={handlePageChange}
              totalPages={Math.ceil(totalTrades / tradesPerPage)}
              showIcons={true}
            />
          </div>
        </>
      )}

      <Modal show={isModalOpen} onClose={() => setIsModalOpen(false)} size="7xl">
        <Modal.Header>Trade Details</Modal.Header>
        <Modal.Body>
          {selectedTrade && <DetailedTradeView trade={selectedTrade} onClose={() => setIsModalOpen(false)} darkMode={darkMode} />}
        </Modal.Body>
      </Modal>
      <Modal show={isDeleteModalOpen} size="md" onClose={() => setIsDeleteModalOpen(false)} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Are you sure you want to delete all trades before {selectedDate.toLocaleDateString()}?
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={handleDeleteOldTrades}>
                Yes, I'm sure
              </Button>
              <Button color="gray" onClick={() => setIsDeleteModalOpen(false)}>
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </main>
  );
};

export default AutoHistory;
